'use client'

import { Loader2, ShieldOff, ShieldCheck, RefreshCw, Wifi, WifiOff, Zap, Users, ChevronDown, ChevronUp } from 'lucide-react'
import { useCircuitBreaker } from '@/contexts/CircuitBreakerContext'
import { useState, useEffect } from 'react'
import { useToast } from '@/hooks/use-toast'
import { useLanguage } from '@/contexts/LanguageContext'

export function NocAgentStatusCard() {
  const {
    circuit_break_active,
    reason,
    triggered_at,
    triggered_by_user,
    public_ip,
    live_is_active,
    protected_apps_before,
    protected_apps_after,
    agents_suspended,
    suspended_at,
    suspended_agents,
    loaded,
    verifying,
    verify,
  } = useCircuitBreaker()
  const { t } = useLanguage()
  const { toast } = useToast()

  const [localVerifying, setLocalVerifying] = useState(false)
  const [showApps, setShowApps] = useState(false)
  const [justRestored, setJustRestored] = useState(false)
  const [pulseActive, setPulseActive] = useState(false)

  const [showAgentList, setShowAgentList] = useState(false)

  useEffect(() => {
    const id = setInterval(() => setPulseActive(p => !p), 1200)
    return () => clearInterval(id)
  }, [])

  const handleVerify = async () => {
    setLocalVerifying(true)
    const result = await verify()
    setLocalVerifying(false)
    if (result.restored) {
      setJustRestored(true)
      setTimeout(() => setJustRestored(false), 4000)
      toast({ title: t.nocStatus.toastResolvedTitle, description: t.nocStatus.toastResolvedDesc })
    } else {
      toast({ title: t.nocStatus.toastAlreadyResolvedTitle, description: t.nocStatus.toastAlreadyResolvedDesc, variant: 'default' })
    }
  }

  if (!loaded) return null

  const formattedCbTime = triggered_at
    ? new Date(triggered_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null
  const formattedSuspTime = suspended_at
    ? new Date(suspended_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null

  const appsToShow = protected_apps_before.length > 0
    ? protected_apps_before.map(app => {
        const after = protected_apps_after.find(a => a.name === app.name)
        return {
          name: app.name,
          before: app.status === 'accessible' ? t.nocStatus.active : app.status,
          after: after
            ? (after.status === 'session_terminated' ? t.nocStatus.terminated : after.status)
            : (circuit_break_active ? t.nocStatus.terminated : t.nocStatus.active),
        }
      })
    : []

  // ── Left panel: Cloudbrink / Circuit Breaker ──────────────────────────────
  const CloudbrinkPanel = () => {
    if (justRestored || !circuit_break_active) {
      return (
        <div className={`flex-1 flex flex-col gap-2 px-4 py-3 rounded-xl border transition-all duration-700 ${
          justRestored ? 'bg-gradient-to-br from-green-50 to-white border-green-200' : 'bg-gradient-to-br from-[#F0FDF4] to-white border-green-100'
        }`}>
          <div className="flex items-center gap-2.5">
            <div className="relative h-8 w-8 flex items-center justify-center shrink-0">
              <div className={`absolute inset-0 rounded-full bg-green-100 ${justRestored ? 'animate-ping opacity-50' : ''}`} />
              <div className="relative h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-green-600" />
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className={`w-5 h-0.5 ${justRestored ? 'bg-green-400' : 'bg-green-300'} rounded-full`} />
              <Wifi className="h-3.5 w-3.5 text-green-500" />
              <div className={`w-5 h-0.5 ${justRestored ? 'bg-green-400' : 'bg-green-300'} rounded-full`} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wide">Cloudbrink</p>
              <p className="text-xs font-medium text-[#313131]">
                {justRestored ? t.nocStatus.sessionRestored : t.nocStatus.operational}
              </p>
            </div>
            <span className={`h-2 w-2 rounded-full shrink-0 ${justRestored ? 'bg-green-400 animate-pulse' : 'bg-green-500'}`} />
          </div>
          {live_is_active === false && (
            <p className="text-[10px] text-amber-600 px-1">{t.nocStatus.liveSessionInactive}</p>
          )}
        </div>
      )
    }

    return (
      <div className="flex-1 rounded-xl bg-gradient-to-br from-[#FEF2F2] to-white border border-red-200 overflow-hidden">
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-start gap-2.5">
            <div className="shrink-0 flex items-center gap-1">
              <div className="relative h-8 w-8 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-red-100 animate-pulse opacity-70" />
                <div className="relative h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                  <ShieldOff className="h-4 w-4 text-red-600" />
                </div>
              </div>
              <div className="flex items-center gap-0.5">
                <div className="w-3 h-0.5 bg-red-400 rounded-full" />
                <WifiOff className="h-3 w-3 text-red-500" />
                <div className="w-3 h-0.5 bg-gray-300 rounded-full" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">Cloudbrink</p>
                <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700 px-1 py-0.5 rounded">
                  <Zap className="h-2 w-2" />{t.nocStatus.circuitBroken}
                </span>
              </div>
              <p className="text-xs font-semibold text-[#141A42] mt-0.5 truncate">{reason || t.nocStatus.isolatedBySOC}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-0 mt-0.5">
                {triggered_by_user && (
                  <p className="text-[10px] text-[#676767]">{t.nocStatus.triggeredBy} <span className="font-medium text-[#313131]">{triggered_by_user}</span></p>
                )}
                {formattedCbTime && (
                  <p className="text-[10px] text-[#676767]">{t.nocStatus.triggeredAt} <span className="font-medium text-[#313131]">{formattedCbTime}</span></p>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={handleVerify}
            disabled={localVerifying || verifying}
            className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-[#e0e0e0] text-[#535EA4] hover:bg-[#f0f3ff] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {localVerifying || verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {t.nocStatus.markAsResolved}
          </button>
        </div>

        {appsToShow.length > 0 && (
          <div className="border-t border-red-100">
            <button
              onClick={() => setShowApps(v => !v)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs font-medium text-[#676767] hover:bg-red-50 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${pulseActive ? 'bg-red-500' : 'bg-red-300'} transition-colors duration-700`} />
                {t.nocStatus.appsLabel} ({appsToShow.length}) — {showApps ? t.nocStatus.hideBeforeAfter : t.nocStatus.showBeforeAfter}
              </span>
              <span className="text-[#535EA4]">{showApps ? '▲' : '▼'}</span>
            </button>
            {showApps && (
              <div className="px-4 pb-3">
                <div className="rounded-lg overflow-hidden border border-red-100">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FEF2F2]">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold text-[#676767] uppercase tracking-wide" style={{ fontSize: '10px' }}>App</th>
                        <th className="text-center px-3 py-2 font-semibold text-green-700 uppercase tracking-wide" style={{ fontSize: '10px' }}>{t.nocStatus.before}</th>
                        <th className="text-center px-3 py-2 font-semibold text-red-700 uppercase tracking-wide" style={{ fontSize: '10px' }}>{t.nocStatus.after}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-red-50">
                      {appsToShow.map((app, i) => (
                        <tr key={i} className="bg-white hover:bg-red-50 transition-colors">
                          <td className="px-3 py-2 font-medium text-[#313131]">{app.name}</td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center gap-1 text-green-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />{app.before}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="inline-flex items-center gap-1 text-red-700">
                              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />{app.after}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {public_ip && (
                  <p className="text-[10px] text-[#99A1AF] mt-1.5 px-1">{t.nocStatus.publicIp} {public_ip}</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // ── Right panel: Agent Suspension ─────────────────────────────────────────
  const AgentSuspensionPanel = () => {
    if (!agents_suspended) {
      return (
        <div className="flex-1 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-gradient-to-br from-[#F0FDF4] to-white border border-green-100">
          <div className="relative h-8 w-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
            <Users className="h-4 w-4 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold text-green-700 uppercase tracking-wide">NOC Agents</p>
            <p className="text-xs font-medium text-[#313131]">Operational</p>
          </div>
          <span className="h-2 w-2 rounded-full bg-green-500 shrink-0" />
        </div>
      )
    }

    return (
      <div className="flex-1 rounded-xl bg-gradient-to-br from-[#FEF2F2] to-white border border-red-200 overflow-hidden">
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-start gap-2.5">
            <div className="relative h-8 w-8 flex items-center justify-center shrink-0">
              <div className="absolute inset-0 rounded-full bg-red-100 animate-pulse opacity-70" />
              <div className="relative h-8 w-8 rounded-full bg-red-100 flex items-center justify-center">
                <Users className="h-4 w-4 text-red-600" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wide">NOC Agents</p>
                <span className="inline-flex text-[9px] font-bold uppercase tracking-wide bg-red-100 text-red-700 px-1 py-0.5 rounded">
                  SUSPENDED
                </span>
              </div>
              <p className="text-xs font-semibold text-[#141A42] mt-0.5">
                {suspended_agents.length > 0
                  ? `${suspended_agents.length} agent${suspended_agents.length !== 1 ? 's' : ''} suspended`
                  : 'Agents suspended'}
              </p>
              {formattedSuspTime && (
                <p className="text-[10px] text-[#676767] mt-0.5">Since <span className="font-medium text-[#313131]">{formattedSuspTime}</span></p>
              )}
            </div>
          </div>

          {/* Agent list toggle — data comes live from Symphony Trust via context */}
          {suspended_agents.length > 0 && (
            <button
              onClick={() => setShowAgentList(v => !v)}
              className="mt-2 w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${pulseActive ? 'bg-red-500' : 'bg-red-300'} transition-colors duration-700`} />
                {showAgentList ? 'Hide agents' : `Show ${suspended_agents.length} suspended agents`}
              </span>
              {showAgentList ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}

          {showAgentList && suspended_agents.length > 0 && (
            <div className="mt-2 rounded-lg border border-red-100 overflow-hidden">
              <ul className="divide-y divide-red-50">
                {suspended_agents.map((agent) => (
                  <li key={agent.id} className="flex items-center justify-between px-3 py-1.5 bg-white hover:bg-red-50 transition-colors">
                    <span className="text-xs text-[#313131] font-medium truncate">{agent.name || agent.id}</span>
                    <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded font-medium ml-2 shrink-0">Suspended</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3">
      <CloudbrinkPanel />
      <AgentSuspensionPanel />
    </div>
  )
}
